import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styles from "./FeedbackChartByNumber.module.scss";
import ChartModal from "../ChartModal/ChartModal";
import _ from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Props {
  data: {
    job: any;
    sessionLogs: any[];
  };
}

const FeedbackChartByNumber: React.FC<Props> = ({ data }) => {
  const [isModalActive, setIsModalActive] = useState(false);
  const [jobData, setJobData] = useState<any>(null);
  const [textLogData, setTextLogData] = useState<any>(null);
  const [sessionLogData, setSessionLogData] = useState<any>(null);

  const labels = useMemo(() => {
    return data.sessionLogs.map((item: any) => {
      return item.createdAt?.split("T")[0] || `ID-${item.id}`;
    });
  }, [data]);

  const dataValues = data.sessionLogs.map(() => 1);

  const backgroundColors = data.sessionLogs.map((item: any) => {
    if (item.rating === "thumbUp") return "rgba(76, 175, 80, 0.8)";
    if (item.rating === "thumbDown") return "rgba(255, 77, 77, 0.8)";
    return "rgba(176, 176, 176, 0.8)";
  });

  const chartData: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: "Feedback",
        data: dataValues,
        backgroundColor: backgroundColors,
        barPercentage: 1,
        categoryPercentage: 1,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    indexAxis: "x" as const,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: false,
        beginAtZero: true,
        max: 1,
        title: { display: false, text: "Feedback (Each Bar = 1 Feedback)" },
        ticks: {
          stepSize: 1,
        },
      },
      x: {
        title: { display: false, text: "Date or Feedback ID" },
        ticks: {
          display: false,
        },
      },
    },
    onClick(event, elements) {
      if (elements.length > 0) {
        const datasetIndex = elements[0].index;
        const sessionLogData = data?.sessionLogs[datasetIndex];
        setTextLogData(sessionLogData?.TextLog);
        setJobData(data?.job);
        setSessionLogData(_.omit(sessionLogData, ["TextLog"]));
        setIsModalActive(true);
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = data?.sessionLogs[ctx.dataIndex];
            return `Feedback: ${item?.rating || "No Feedback"} - Date: ${
              item?.createdAt
            }`;
          },
        },
      },
      title: { display: false, text: "Individual Feedback" },
    },
  };

  return (
    <React.Fragment>
      <ChartModal
        isOpen={isModalActive}
        setIsOpen={setIsModalActive}
        jobData={jobData}
        textLogData={textLogData}
        sessionLogData={sessionLogData}
      />
      <div className={styles.mainContainer}>
        <div className={styles.addressContainer}>
          <p className={styles.address}>{data?.job?.address}</p>
        </div>

        <div className={styles.chartContainer}>
          <Bar options={options} data={chartData} />
        </div>
      </div>
    </React.Fragment>
  );
};

export default FeedbackChartByNumber;
